/**
 * Pure geometry and compression utilities for detection bounding polys.
 *
 * Works on the 4-point axis-aligned BoundingPolyRect shape (normalized 0..1 coordinates)
 * from src/common-app/state/modules/mediaDetails/models. Provides optimize/fuse to merge
 * time-adjacent, near-identical polys in engine results into fewer spans, intersection and
 * area math (intersection over union), scale/descale helpers (descale inverts scale's
 * percent growth), poly-center conversions, and precision-rounding helpers that trim
 * coordinates and times to the constants in @helpers/constants before saving assets.
 * Note: decimalPrecisionSavingBoundingPoly mutates the poly objects passed in.
 */
import {
  flatten,
  groupBy,
  mapValues,
  sortBy,
  values,
  assign,
  clamp,
  isNumber,
} from 'lodash';

import {
  BoundingPoly,
  BoundingPolyRect,
  PolyCenter,
  UDRsPolyAssetGroup,
  BlurEngineUDRsPolyAsset,
  EditablePoint,
  Point,
} from '@common-modules/mediaDetails/models';
import { GetFaceEngineResultsResponse } from '@worker';
import { BOUNDING_POLY_PRECISION, TIME_MS_PRECISION } from '@helpers/constants';
import { UDRsPolyAssetGroupV2 } from '@common/web-worker/services/adapters/optical-tracking/asset-v2';
import { arrayHasContents } from '@common/shared/util';
import { DeepReadonly } from './types';

export type Optimizable = Pick<BoundingPoly, 'startTimeMs' | 'stopTimeMs'> & {
  object: DeepReadonly<Pick<BoundingPoly['object'], 'boundingPoly'>>;
};

export const optimizePolys = (
  record: GetFaceEngineResultsResponse
): GetFaceEngineResultsResponse => ({
  ...record,
  jsondata: {
    ...record.jsondata,
    // series: r.jsondata.series.map(item => {
    series: sortBy(
      flatten(
        values(
          mapValues(
            groupBy(record.jsondata.series, (s) => s.object.label),
            optimize
          )
        )
      ),
      'startTimeMs'
    ),
  },
});

/**
 * Optimize and compress a group of BoundingPolys.
 */
export const optimize = <O extends Optimizable>(
  polys: ReadonlyArray<O>
): ReadonlyArray<O> => {
  const optPolys: Array<O> = [];
  let consecPolys: Array<O> = [];
  for (const poly of polys) {
    if (withinTolerance(consecPolys, poly)) {
      consecPolys.push(poly);
    } else {
      optPolys.push(fuse(consecPolys));
      consecPolys = [poly];
    }
  }
  optPolys.push(fuse(consecPolys));
  return optPolys;
};

const withinTolerance = <O extends Optimizable>(
  a: ReadonlyArray<O>,
  b: O
): boolean => {
  if (!arrayHasContents(a)) {
    return true;
  }
  const last = a.at(-1)!; // valid due to array having contents
  const first = a[0];
  if (last.stopTimeMs !== b.startTimeMs) {
    return false;
  }

  let d = 0;
  for (let i = 0; i < first.object.boundingPoly.length; i++) {
    const { x: x0, y: y0 } = first.object.boundingPoly[i]!; // Safe due to previous length check
    const { x: x1, y: y1 } = b.object.boundingPoly[i]!; // Safe due to previous length check
    d += Math.abs(x0 - x1) + Math.abs(y0 - y1);
    if (d >= 0.1) {
      return false;
    }
  }
  return true;
};

/**
 * Merge multiple polys.
 */
export const fuse = <O extends Optimizable>(ps: ReadonlyArray<O>): O => {
  if (!arrayHasContents(ps)) {
    throw new Error('Cannot fuse zero polys');
  }
  const first = ps[0];
  let startTimeMs = first.startTimeMs;
  let stopTimeMs = first.stopTimeMs;
  const boundingPoly: [
    EditablePoint,
    EditablePoint,
    EditablePoint,
    EditablePoint,
  ] = [
    { x: 1, y: 1 },
    { x: 0, y: 1 },
    { x: 0, y: 0 },
    { x: 1, y: 0 },
  ];
  for (const p of ps) {
    startTimeMs = Math.min(startTimeMs, p.startTimeMs);
    stopTimeMs = Math.max(stopTimeMs, p.stopTimeMs);
    const bps = p.object.boundingPoly;

    // bps access is safe due to being a boundingPolyRect - just can't get the
    // types to work fully correctly in Optimizable
    boundingPoly[0].x = Math.min(boundingPoly[0].x, bps[0].x);
    boundingPoly[0].y = Math.min(boundingPoly[0].y, bps[0].y);

    boundingPoly[1].x = Math.max(boundingPoly[1].x, bps[1].x);
    boundingPoly[1].y = Math.min(boundingPoly[1].y, bps[1].y);

    boundingPoly[2].x = Math.max(boundingPoly[2].x, bps[2].x);
    boundingPoly[2].y = Math.max(boundingPoly[2].y, bps[2].y);

    boundingPoly[3].x = Math.min(boundingPoly[3].x, bps[3].x);
    boundingPoly[3].y = Math.max(boundingPoly[3].y, bps[3].y);
  }

  return {
    ...first,
    startTimeMs,
    stopTimeMs,
    object: {
      ...first.object,
      boundingPoly,
    },
  };
};

// check if two bounding polys are the same
export const checkSameBoundingPoly = (
  boundingPoly1: BoundingPolyRect,
  boundingPoly2: BoundingPolyRect
): boolean => {
  // only need to check two opposite corners on diagonal
  if (
    boundingPoly1[0].x === boundingPoly2[0].x &&
    boundingPoly1[0].y === boundingPoly2[0].y &&
    boundingPoly1[2].x === boundingPoly2[2].x &&
    boundingPoly1[2].y === boundingPoly2[2].y
  ) {
    return true;
  } else {
    return false;
  }
};

// intersection over union
export const intersectionOverUnion = (
  boundingPoly1: BoundingPolyRect,
  boundingPoly2: BoundingPolyRect
): number => {
  const int_x =
    Math.min(boundingPoly1[1].x, boundingPoly2[1].x) -
    Math.max(boundingPoly1[0].x, boundingPoly2[0].x);
  const int_y =
    Math.min(boundingPoly1[2].y, boundingPoly2[2].y) -
    Math.max(boundingPoly1[1].y, boundingPoly2[1].y);

  const un_x =
    Math.max(boundingPoly1[1].x, boundingPoly2[1].x) -
    Math.min(boundingPoly1[0].x, boundingPoly2[0].x);
  const un_y =
    Math.max(boundingPoly1[2].y, boundingPoly2[2].y) -
    Math.min(boundingPoly1[1].y, boundingPoly2[1].y);

  const un_area = un_x * un_y;
  const int_area = int_x * int_y;

  let iou = 0;
  if (int_x > 0 && int_y > 0) {
    iou = int_area / un_area;
  }
  return iou;
};

export const intersectionArea = (
  boundingPoly1: BoundingPolyRect,
  boundingPoly2: BoundingPolyRect
): number => {
  const int_x =
    Math.min(boundingPoly1[1].x, boundingPoly2[1].x) -
    Math.max(boundingPoly1[0].x, boundingPoly2[0].x);
  const int_y =
    Math.min(boundingPoly1[2].y, boundingPoly2[2].y) -
    Math.max(boundingPoly1[1].y, boundingPoly2[1].y);

  if (int_x > 0 && int_y > 0) {
    return int_x * int_y;
  } else {
    return 0;
  }
};

export const boundingPolyArea = (boundingPoly1: BoundingPolyRect): number => {
  const x = boundingPoly1[1].x - boundingPoly1[0].x;
  const y = boundingPoly1[2].y - boundingPoly1[1].y;

  return x * y;
};

/**
 * Scale boundingPolyObject relative to current size
 */
export const scaleBoundingPolyObject =
  (PERCENT_CHANGE: number) =>
  <T extends { object: Pick<BoundingPoly['object'], 'boundingPoly'> }>(
    initItem: T
  ) => {
    let item = initItem;

    const boundingPoly = scaleBoundingPolyRect(
      item.object.boundingPoly,
      PERCENT_CHANGE
    );

    // update boundingPoly
    item = assign({}, item, {
      object: assign({}, item.object, {
        boundingPoly,
      }),
    });

    return item;
  };

const clampPoint = (point: Point) => ({
  x: clamp(point.x, 0, 1),
  y: clamp(point.y, 0, 1),
});

export const scaleAndClampBoundingPolyRect = (
  boundingPoly: BoundingPolyRect,
  PERCENT_CHANGE = 0,
  decimalPrecision = 0
): BoundingPolyRect => {
  const rect = scaleBoundingPolyRect(
    boundingPoly,
    PERCENT_CHANGE,
    decimalPrecision
  );
  return [
    clampPoint(rect[0]),
    clampPoint(rect[1]),
    clampPoint(rect[2]),
    clampPoint(rect[3]),
  ];
};

export const scaleBoundingPolyRect = (
  boundingPoly: BoundingPolyRect,
  PERCENT_CHANGE = 0,
  decimalPrecision = 0
): BoundingPolyRect => {
  const dx =
    ((boundingPoly[1].x - boundingPoly[0].x) * PERCENT_CHANGE) / 100 / 2;
  const dy =
    ((boundingPoly[2].y - boundingPoly[1].y) * PERCENT_CHANGE) / 100 / 2;

  if (decimalPrecision > 0) {
    return [
      {
        x: Number((boundingPoly[0].x - dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[0].y - dy).toFixed(decimalPrecision)),
      },
      {
        x: Number((boundingPoly[1].x + dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[1].y - dy).toFixed(decimalPrecision)),
      },
      {
        x: Number((boundingPoly[2].x + dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[2].y + dy).toFixed(decimalPrecision)),
      },
      {
        x: Number((boundingPoly[3].x - dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[3].y + dy).toFixed(decimalPrecision)),
      },
    ];
  }

  return [
    { x: boundingPoly[0].x - dx, y: boundingPoly[0].y - dy },
    { x: boundingPoly[1].x + dx, y: boundingPoly[1].y - dy },
    { x: boundingPoly[2].x + dx, y: boundingPoly[2].y + dy },
    { x: boundingPoly[3].x - dx, y: boundingPoly[3].y + dy },
  ];
};

export const descaleBoundingPolyRect = (
  boundingPoly: BoundingPolyRect,
  PERCENT_CHANGE = 0,
  decimalPrecision = 0
): BoundingPolyRect => {
  if (PERCENT_CHANGE === 0) {
    return boundingPoly;
  }
  const dx =
    ((boundingPoly[1].x - boundingPoly[0].x) * PERCENT_CHANGE) /
    (2 * (100 + PERCENT_CHANGE));
  const dy =
    ((boundingPoly[2].y - boundingPoly[1].y) * PERCENT_CHANGE) /
    (2 * (100 + PERCENT_CHANGE));

  if (decimalPrecision > 0) {
    return [
      {
        x: Number((boundingPoly[0].x + dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[0].y + dy).toFixed(decimalPrecision)),
      },
      {
        x: Number((boundingPoly[1].x - dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[1].y + dy).toFixed(decimalPrecision)),
      },
      {
        x: Number((boundingPoly[2].x - dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[2].y - dy).toFixed(decimalPrecision)),
      },
      {
        x: Number((boundingPoly[3].x + dx).toFixed(decimalPrecision)),
        y: Number((boundingPoly[3].y - dy).toFixed(decimalPrecision)),
      },
    ];
  }

  return [
    { x: boundingPoly[0].x + dx, y: boundingPoly[0].y + dy },
    { x: boundingPoly[1].x - dx, y: boundingPoly[1].y + dy },
    { x: boundingPoly[2].x - dx, y: boundingPoly[2].y - dy },
    { x: boundingPoly[3].x + dx, y: boundingPoly[3].y - dy },
  ];
};

/**
 * Create boundingPolyRect using polyCenter
 */
export const boundingPolyFromPolyCenter = (
  polyCenter: PolyCenter,
  PERCENT_CHANGE = 0,
  decimalPrecision = 3
): BoundingPolyRect => {
  let halfWidth = 0,
    halfHeight = 0;

  halfWidth = (polyCenter.width * (1 + PERCENT_CHANGE / 100)) / 2;
  halfHeight = (polyCenter.height * (1 + PERCENT_CHANGE / 100)) / 2;

  if (decimalPrecision > 0) {
    return [
      {
        x: Number(
          clamp(polyCenter.x - halfWidth, 0, 1).toFixed(decimalPrecision)
        ),
        y: Number(
          clamp(polyCenter.y - halfHeight, 0, 1).toFixed(decimalPrecision)
        ),
      },
      {
        x: Number(
          clamp(polyCenter.x + halfWidth, 0, 1).toFixed(decimalPrecision)
        ),
        y: Number(
          clamp(polyCenter.y - halfHeight, 0, 1).toFixed(decimalPrecision)
        ),
      },
      {
        x: Number(
          clamp(polyCenter.x + halfWidth, 0, 1).toFixed(decimalPrecision)
        ),
        y: Number(
          clamp(polyCenter.y + halfHeight, 0, 1).toFixed(decimalPrecision)
        ),
      },
      {
        x: Number(
          clamp(polyCenter.x - halfWidth, 0, 1).toFixed(decimalPrecision)
        ),
        y: Number(
          clamp(polyCenter.y + halfHeight, 0, 1).toFixed(decimalPrecision)
        ),
      },
    ];
  }

  return [
    {
      x: clamp(polyCenter.x - halfWidth, 0, 1),
      y: clamp(polyCenter.y - halfHeight, 0, 1),
    },
    {
      x: clamp(polyCenter.x + halfWidth, 0, 1),
      y: clamp(polyCenter.y - halfHeight, 0, 1),
    },
    {
      x: clamp(polyCenter.x + halfWidth, 0, 1),
      y: clamp(polyCenter.y + halfHeight, 0, 1),
    },
    {
      x: clamp(polyCenter.x - halfWidth, 0, 1),
      y: clamp(polyCenter.y + halfHeight, 0, 1),
    },
  ];
};

export const polyCenterFromBoundingPoly = (
  boundingPoly: BoundingPolyRect
): PolyCenter => {
  const polyCenter = {
    x: (boundingPoly[1].x + boundingPoly[0].x) / 2,
    y: (boundingPoly[2].y + boundingPoly[0].y) / 2,
    width: boundingPoly[1].x - boundingPoly[0].x,
    height: boundingPoly[2].y - boundingPoly[1].y,
  };

  return polyCenter;
};

// saveRedactionSelections function uses the UDRsAssetV2 which has an updated series element
// while saveMediaData function uses UDRsAsset this function must be compatible with either types
export const decimalPrecisionSavingBoundingPoly = (boundingPolys?: {
  [groupId: string]:
    | UDRsPolyAssetGroup
    | UDRsPolyAssetGroupV2
    | BlurEngineUDRsPolyAsset
    | null;
}) => {
  flatten(values(boundingPolys)).forEach((boundingPoly) => {
    if (boundingPoly?.basePoly) {
      boundingPoly.basePoly.object.boundingPoly?.forEach((point) => {
        const newPoint = {
          x: Number(point.x.toFixed(BOUNDING_POLY_PRECISION)),
          y: Number(point.y.toFixed(BOUNDING_POLY_PRECISION)),
        };
        Object.assign(point, newPoint);
      });
      if (
        boundingPoly.basePoly.startTimeMs !== undefined &&
        boundingPoly.basePoly.stopTimeMs !== undefined
      ) {
        const startTimeBase = Number(
          boundingPoly.basePoly.startTimeMs.toFixed(TIME_MS_PRECISION)
        );
        const stopTimeBase = Number(
          boundingPoly.basePoly.stopTimeMs.toFixed(TIME_MS_PRECISION)
        );
        Object.assign(boundingPoly.basePoly, {
          startTimeMs: startTimeBase,
          stopTimeMs: stopTimeBase,
        });
      }
    }
    boundingPoly?.series.forEach((seriesBp) => {
      seriesBp?.object.boundingPoly.forEach((point) => {
        const newPoint = {
          x: Number(point.x.toFixed(BOUNDING_POLY_PRECISION)),
          y: Number(point.y.toFixed(BOUNDING_POLY_PRECISION)),
        };
        Object.assign(point, newPoint);
      });
      const startTimeMs = Number(
        seriesBp.startTimeMs.toFixed(TIME_MS_PRECISION)
      );
      const stopTimeMs = Number(seriesBp.stopTimeMs.toFixed(TIME_MS_PRECISION));
      Object.assign(seriesBp, { startTimeMs, stopTimeMs });
    });
  });
};

const NumericKeys = ['x', 'y'];
const TimeKeys = ['startTimeMs', 'stopTimeMs'];

export const boundingPolyPrecisionAdjustReplacer = (
  key: string,
  value: unknown
) => {
  if (NumericKeys.includes(key)) {
    if (isNumber(value)) {
      return Number(value.toFixed(BOUNDING_POLY_PRECISION));
    }
  } else if (TimeKeys.includes(key)) {
    if (isNumber(value)) {
      return Number(value.toFixed(TIME_MS_PRECISION));
    }
  }
  return value;
};
